import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";
import {
  Shield,
  ShieldCheck,
  UserCog,
  FileSignature,
  Send,
  Fingerprint,
  Users,
  ScrollText,
  Settings,
  Code2,
  KeyRound,
  Search,
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  ArrowRight,
  Check,
  Minus,
  X,
  Info,
  Headphones,
  BookOpen,
  CheckCircle2,
} from "lucide-react";

/* ==========================================================================
   Styles
   ========================================================================== */

function RolesManagementStyles() {
  return (
    <style>{`
.rm-app {
  --rm-bg: #f7f8fb;
  --rm-surface: #ffffff;
  --rm-border: #e7e9f0;
  --rm-text: #16192c;
  --rm-text-muted: #6b7183;
  --rm-text-faint: #9298a8;
  --rm-primary: #5b4bf5;
  --rm-primary-light: #eeecfe;
  --rm-primary-hover: #4a3ce0;

  --rm-green-bg: #e7f8ef;
  --rm-green-border: #b8ecce;
  --rm-green-text: #1a9d55;

  --rm-amber-bg: #fef6e0;
  --rm-amber-border: #fbe4a3;
  --rm-amber-text: #cc9412;

  --rm-red-bg: #fdeceb;
  --rm-red-border: #f8c6c1;
  --rm-red-text: #e0463c;

  --rm-radius-sm: 6px;
  --rm-radius-md: 10px;
  --rm-radius-lg: 14px;

  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: var(--rm-text);
  background: var(--rm-bg);
  font-size: 13px;
  line-height: 1.45;
  box-sizing: border-box;
  width: 100%;
  overflow-x: hidden;
}

.rm-app *, .rm-app *::before, .rm-app *::after { box-sizing: border-box; }

/* Topbar */
.rm-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid var(--rm-border);
}

.rm-tabs { display: flex; gap: 24px; }

.rm-tab {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  color: #64748b;
  padding-bottom: 10px;
  border-bottom: 2px solid transparent;
  cursor: pointer;
}
.rm-tab.active { color: var(--rm-primary); border-bottom-color: var(--rm-primary); }

.rm-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  border-radius: var(--rm-radius-sm);
  padding: 7px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  transition: background .15s ease, border-color .15s ease, color .15s ease;
}
.rm-btn-primary { background: var(--rm-primary); color: #fff; }
.rm-btn-primary:hover { background: var(--rm-primary-hover); }
.rm-btn-outline { background: #fff; border-color: var(--rm-border); color: var(--rm-primary); }
.rm-btn-outline:hover { background: var(--rm-primary-light); }
.rm-btn-outline-danger { background: #fff; border-color: #fbd6d3; color: #e0463c; }
.rm-btn-outline-danger:hover { background: var(--rm-red-bg); }
.rm-btn-sm { padding: 5px 10px; font-size: 12px; }

/* Body layout */
.rm-body {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px 12px;
  width: 100%;
  min-width: 0;
}

/* Search */
.rm-search { position: relative; }
.rm-search-icon { position: absolute; top: 50%; left: 11px; transform: translateY(-50%); color: var(--rm-text-faint); }
.rm-search input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e2e8f0;
  border-radius: var(--rm-radius-sm);
  font-size: 13px;
  font-family: inherit;
  color: #475569;
  background: var(--rm-bg);
  outline: none;
}
.rm-search input:focus { border-color: var(--rm-primary); background: #fff; }

/* Left sidebar: role list */
.rm-sidebar {
  flex: 0 0 200px;
  min-width: 0;
  background: var(--rm-surface);
  border: 1px solid var(--rm-border);
  border-radius: var(--rm-radius-lg);
  padding: 10px;
}
.rm-panel-title { font-size: 13px; font-weight: 700; color: #0f172a; margin: 0 0 8px; }
.rm-role-list { display: flex; flex-direction: column; gap: 2px; margin-top: 8px; }

.rm-role-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 7px 8px;
  border-radius: var(--rm-radius-sm);
  border: 1px solid transparent;
  background: none;
  cursor: pointer;
}
.rm-role-item:hover { background: var(--rm-bg); }
.rm-role-item.active { background: var(--rm-primary-light); border-color: #d9d4fc; }

.rm-role-icon {
  flex: 0 0 auto;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  background: var(--icon-bg, #eef1ff);
  color: var(--icon-fg, #5b4bf5);
}

.rm-role-text { flex: 1; min-width: 0; }
.rm-role-name-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.rm-role-name { font-size: 13px; font-weight: 600; color: #0f172a; }
.rm-role-item.active .rm-role-name { color: var(--rm-primary); }

.rm-pill {
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--rm-primary-light);
  color: var(--rm-primary);
  white-space: nowrap;
}

.rm-role-desc {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.rm-role-count {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  color: var(--rm-text-faint);
  background: var(--rm-bg);
  border-radius: 999px;
  padding: 1px 8px;
  align-self: center;
}
.rm-role-item.active .rm-role-count { background: #fff; color: var(--rm-primary); }

.rm-sidebar-footer {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--rm-border);
  font-size: 12px;
  color: var(--rm-text-faint);
}

/* Main column */
.rm-main { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; gap: 10px; overflow: hidden; }

.rm-card {
  background: var(--rm-surface);
  border: 1px solid var(--rm-border);
  border-radius: var(--rm-radius-lg);
  padding: 14px;
}

/* Role details card */
.rm-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.rm-details-title { display: flex; align-items: center; gap: 12px; }
.rm-details-avatar {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: var(--icon-bg, #eef1ff);
  color: var(--icon-fg, #5b4bf5);
  flex: 0 0 auto;
}
.rm-details-title h2 { margin: 0; font-size: 15px; font-weight: 700; color: #0f172a; }
.rm-details-actions { display: flex; gap: 8px; }

.rm-details-body { display: flex; gap: 12px; flex-wrap: nowrap; min-width: 0; }

.rm-fields {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rm-field-row { display: flex; gap: 16px; }
.rm-field-label {
  flex: 0 0 130px;
  font-size: 12.5px;
  color: #94a3b8;
  font-weight: 600;
}
.rm-field-value { font-size: 13px; color: #0f172a; font-weight: 500; }

.rm-badge-role-type {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--rm-primary-light);
  color: var(--rm-primary);
}

.rm-users-box {
  flex: 0 0 200px;
  min-width: 0;
  border: 1px solid var(--rm-border);
  border-radius: var(--rm-radius-md);
  padding: 10px;
}
.rm-users-box h4 { margin: 0 0 8px; font-size: 13px; font-weight: 700; color: #0f172a; }

.rm-user-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}
.rm-user-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: var(--rm-primary-light);
  color: var(--rm-primary);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
  flex: 0 0 auto;
}
.rm-user-info { flex: 1; min-width: 0; }
.rm-user-name { font-size: 12.5px; font-weight: 600; color: #0f172a; }
.rm-user-email {
  font-size: 11px; color: #64748b;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.rm-status-pill {
  flex: 0 0 auto;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--rm-green-bg);
  color: var(--rm-green-text);
}

.rm-view-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 6px;
  padding: 6px;
  border-radius: var(--rm-radius-sm);
  background: var(--rm-primary-light);
  color: var(--rm-primary);
  font-size: 12.5px;
  font-weight: 600;
  text-decoration: none;
}
.rm-view-all:hover { background: #e2ddfd; }

/* Permissions card */
.rm-perm-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.rm-perm-toolbar h3 { margin: 0; font-size: 14px; font-weight: 700; color: #0f172a; }
.rm-perm-toolbar-actions { display: flex; gap: 8px; }

.rm-perm-filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.rm-perm-filter-row .rm-search { max-width: 300px; flex: 1 1 240px; }

.rm-legend-inline { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.rm-legend-inline-item { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #64748b; }

.rm-table-wrap {
  border: 1px solid var(--rm-border);
  border-radius: var(--rm-radius-md);
  overflow: hidden;
  width: 100%;
}
.rm-table { width: 100%; border-collapse: collapse; table-layout: fixed; }

.rm-table thead th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  padding: 7px 6px;
  background: #fafbfc;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rm-table thead th.rm-col-category { width: 36%; }

.rm-table tbody tr { border-bottom: 1px solid var(--rm-border); }
.rm-table tbody tr:last-child { border-bottom: none; }
.rm-table tbody tr:hover { background: #fafafe; }
.rm-table td { padding: 6px 6px; vertical-align: middle; overflow: hidden; }

.rm-cat-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rm-cat-chevron {
  color: var(--rm-text-faint);
  transition: transform .15s ease;
  flex: 0 0 auto;
}
.rm-cat-chevron.expanded { transform: rotate(90deg); }
.rm-cat-icon {
  flex: 0 0 auto;
  width: 26px; height: 26px;
  border-radius: 7px;
  background: var(--rm-bg);
  color: var(--rm-text-muted);
  display: flex; align-items: center; justify-content: center;
}
.rm-cat-label { font-size: 13px; font-weight: 600; color: #0f172a; }

.rm-cell { text-align: left; }

.access-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px; height: 22px;
  border-radius: 6px;
  border: 1px solid transparent;
}
.access-full { background: var(--rm-green-bg); border-color: var(--rm-green-border); color: var(--rm-green-text); }
.access-partial { background: var(--rm-amber-bg); border-color: var(--rm-amber-border); color: var(--rm-amber-text); }
.access-none { background: var(--rm-red-bg); border-color: var(--rm-red-border); color: var(--rm-red-text); }

/* Right sidebar */
.rm-right-sidebar {
  flex: 0 0 185px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rm-info-card h4 {
  display: flex; align-items: center; gap: 8px;
  margin: 0 0 6px;
  font-size: 13px; font-weight: 700; color: #0f172a;
}
.rm-info-card p { margin: 0; font-size: 12px; color: #64748b; line-height: 1.5; }

.rm-checklist { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 7px; }
.rm-checklist li { display: flex; align-items: flex-start; gap: 7px; font-size: 12px; color: #334155; font-weight: 500; }
.rm-checklist svg { color: var(--rm-green-text); flex: 0 0 auto; margin-top: 1px; }

.rm-help-links { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.rm-help-links a { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--rm-primary); text-decoration: none; }
.rm-help-links a:hover { text-decoration: underline; }

/* Responsive */
@media (max-width: 1100px) {
  .rm-right-sidebar { display: none; }
}
@media (max-width: 900px) {
  .rm-body { flex-direction: column; }
  .rm-sidebar { flex: 1 1 auto; width: 100%; }
  .rm-details-body { flex-direction: column; }
  .rm-users-box { flex: 1 1 auto; }
}

/* Role modal (Create / Edit) */
.rm-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.rm-modal {
  background: #fff;
  border-radius: var(--rm-radius-lg);
  width: 440px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
.rm-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px 14px;
  border-bottom: 1px solid var(--rm-border);
}
.rm-modal-header h3 { margin: 0; font-size: 15px; font-weight: 700; color: var(--rm-text); }
.rm-modal-close {
  background: none; border: none; cursor: pointer; color: #64748b;
  display: flex; align-items: center; padding: 4px; border-radius: 6px;
}
.rm-modal-close:hover { background: #f1f5f9; color: var(--rm-text); }
.rm-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
.rm-modal-body label { font-size: 12.5px; font-weight: 600; color: #374151; margin-bottom: -8px; }
.rm-modal-body input,
.rm-modal-body textarea {
  width: 100%; padding: 9px 11px; border: 1px solid var(--rm-border);
  border-radius: 8px; font-size: 13.5px; color: var(--rm-text); outline: none;
  font-family: inherit; resize: vertical;
}
.rm-modal-body input:focus,
.rm-modal-body textarea:focus { border-color: var(--rm-primary); }
.rm-modal-hint { font-size: 11.5px; color: #94a3b8; margin-top: -8px; }
.rm-modal-error {
  background: var(--rm-red-bg); border: 1px solid var(--rm-red-border);
  color: var(--rm-red-text); border-radius: 8px; padding: 8px 12px; font-size: 12.5px;
}
.rm-modal-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 22px 20px; border-top: 1px solid var(--rm-border);
}
`}</style>
  );
}

/* ==========================================================================
   Data
   ========================================================================== */

// Display-only metadata the API doesn't (and shouldn't) send -- icon and
// color per role/category. Keyed by the backend's role_code / category_code
// so real data from /api/v1/roles and /api/v1/permission-matrix slots
// straight in. Anything not listed here (a newly created custom role or
// category) falls back to a neutral icon/color.
const ROLE_ICON = {
  SUPER_ADMIN: { icon: Shield, iconBg: "#f1edfe", iconFg: "#5b4bf5" },
  ADMINISTRATOR: { icon: ShieldCheck, iconBg: "#e9f1ff", iconFg: "#2563eb" },
  SIGNING_MANAGER: { icon: FileSignature, iconBg: "#e8f8ef", iconFg: "#1a9d55" },
  CERTIFICATE_MANAGER: { icon: Fingerprint, iconBg: "#fdf1e6", iconFg: "#d97706" },
  COMPLIANCE_OFFICER: { icon: ScrollText, iconBg: "#e7f7fa", iconFg: "#0891b2" },
  AUDITOR: { icon: UserCog, iconBg: "#eef0fe", iconFg: "#4f46e5" },
  STANDARD_USER: { icon: Users, iconBg: "#f1f5f9", iconFg: "#64748b" },
  API_USER: { icon: KeyRound, iconBg: "#f1f5f9", iconFg: "#64748b" },
};
const DEFAULT_ROLE_ICON = { icon: Users, iconBg: "#f1f5f9", iconFg: "#64748b" };

const CATEGORY_ICON = {
  DASHBOARD: UserCog,
  DOCUMENT_SIGNING: FileSignature,
  SIGNATURE_REQUEST: Send,
  DOCUMENT_VERIFY: ShieldCheck,
  CERTIFICATE: Fingerprint,
  USER_GROUP: Users,
  ROLE_PERMISSION: UserCog,
  AUDIT: ScrollText,
  SYSTEM: Settings,
  INTEGRATION: Code2,
  HSM: KeyRound,
};
const DEFAULT_CATEGORY_ICON = UserCog;

// role_code -> a stable slug used for the sidebar/table state (e.g. "super-admin")
const slugify = (code) => code.toLowerCase().replace(/_/g, "-");

// Reduce a category's per-permission access levels (from the matrix) to one
// representative level per role for the category-level row on this screen.
// FULL only if every permission is FULL, NONE only if every permission is
// NONE, PARTIAL otherwise -- mirrors how the spec's category-level matrix
// (section 9) relates to the per-permission grants underneath it.
function summarizeCategoryAccess(permissions, roleCode) {
  const levels = permissions.map((p) => p.access[roleCode] || "NONE");
  if (levels.every((l) => l === "FULL")) return "full";
  if (levels.every((l) => l === "NONE")) return "none";
  return "partial";
}

const mergeRoleList = (items) =>
  items.map((r) => ({
    id: slugify(r.role_code),
    role_id: r.role_id,
    role_code: r.role_code,
    name: r.name,
    summary: r.description || "",
    description: r.description || "",
    systemRole: r.is_system_role,
    users: r.user_count || 0,
    createdOn: r.created_at ? new Date(r.created_at).toLocaleString() : "—",
    lastUpdated: r.updated_at ? new Date(r.updated_at).toLocaleString() : "—",
    userList: [],
    ...(ROLE_ICON[r.role_code] || DEFAULT_ROLE_ICON),
  }));

const mergeCategoryList = (matrixCategories, matrixRoles) =>
  matrixCategories.map((c) => ({
    id: c.category_code.toLowerCase(),
    label: c.name,
    icon: CATEGORY_ICON[c.category_code] || DEFAULT_CATEGORY_ICON,
    access: matrixRoles.reduce((acc, role) => {
      acc[slugify(role.role_code)] = summarizeCategoryAccess(c.permissions, role.role_code);
      return acc;
    }, {}),
  }));

const submitLabel = (submitting, isCreate) => {
  if (submitting) return "Saving…";
  return isCreate ? "Create Role" : "Save Changes";
};

const ACCESS_META = {
  full: { icon: Check, className: "access-full", label: "Full Access" },
  partial: { icon: Minus, className: "access-partial", label: "Partial Access" },
  none: { icon: X, className: "access-none", label: "No Access" },
};

const BEST_PRACTICES = [
  "Follow least privilege access",
  "Review roles periodically",
  "Use groups for easier management",
  "Enable audit logging",
];

/* ==========================================================================
   Small components
   ========================================================================== */

function AccessBadge({ level }) {
  const meta = ACCESS_META[level] || ACCESS_META.none;
  const Icon = meta.icon;
  return (
    <span className={`access-badge ${meta.className}`} title={meta.label}>
      <Icon size={13} strokeWidth={3} />
    </span>
  );
}

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function RoleModal({ isCreate, form, onField, onNameChange, formError, submitting, onClose, onSubmit }) {
  return (
    <div className="rm-modal-overlay" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-header">
          <h3>{isCreate ? "Create Role" : "Edit Role"}</h3>
          <button className="rm-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="rm-modal-body">
          {formError && <div className="rm-modal-error">{formError}</div>}

          <label>Role Name</label>
          <input
            type="text"
            placeholder="e.g. Regional Manager"
            value={form.name}
            onChange={(e) => onNameChange(e.target.value)}
          />

          <label>Role Code</label>
          <input
            type="text"
            placeholder="e.g. REGIONAL_MANAGER"
            value={form.role_code}
            disabled={!isCreate}
            onChange={(e) => onField("role_code", e.target.value.toUpperCase())}
          />
          {isCreate && (
            <span className="rm-modal-hint">
              Auto-filled from the name — edit if you'd like a different code.
            </span>
          )}

          <label>Description</label>
          <textarea
            rows={3}
            placeholder="What is this role for?"
            value={form.description}
            onChange={(e) => onField("description", e.target.value)}
          />

          {isCreate && (
            <span className="rm-modal-hint">
              New roles start with no permissions granted — set them from the Permissions tab afterward.
            </span>
          )}
        </div>
        <div className="rm-modal-footer">
          <button className="rm-btn rm-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="rm-btn rm-btn-primary" onClick={onSubmit} disabled={submitting}>
            {submitLabel(submitting, isCreate)}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Main component
   ========================================================================== */

export default function RolesManagement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("roles");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [permFilter, setPermFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: "", role_code: "", description: "" });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadRolesAndMatrix = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [rolesRes, matrixRes] = await Promise.all([
        axios.get(`${API_URL}/api/v1/roles`),
        axios.get(`${API_URL}/api/v1/permission-matrix`),
      ]);

      const matrixRoles = matrixRes.data.roles || [];
      const matrixCategories = matrixRes.data.categories || [];

      // GET /api/v1/roles has the user counts and timestamps; the matrix
      // endpoint has per-role, per-permission access. Join them by role_id.
      const mergedRoles = mergeRoleList(rolesRes.data.items || []);
      const mergedCategories = mergeCategoryList(matrixCategories, matrixRoles);

      setRoles(mergedRoles);
      setCategories(mergedCategories);
      setActiveRoleId((prev) => prev || (mergedRoles[0] && mergedRoles[0].id));
      return mergedRoles;
    } catch (err) {
      setLoadError(true);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRolesAndMatrix();
  }, []);

  const roleColumns = roles.map((r) => r.id);

  const filteredRoles = roles.filter((r) =>
    r.name.toLowerCase().includes(roleFilter.toLowerCase())
  );

  const activeRole = roles.find((r) => r.id === activeRoleId) || roles[0];

  const roleCodeFromName = (name) =>
    name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const openCreateModal = () => {
    setRoleForm({ name: "", role_code: "", description: "" });
    setFormError("");
    setShowCreateModal(true);
  };

  const openEditModal = () => {
    if (!activeRole) return;
    setRoleForm({
      name: activeRole.name,
      role_code: activeRole.role_code,
      description: activeRole.description || "",
    });
    setFormError("");
    setShowEditModal(true);
  };

  const closeModals = () => {
    if (submitting) return;
    setShowCreateModal(false);
    setShowEditModal(false);
    setFormError("");
  };

  const handleNameChange = (name) => {
    setRoleForm((prev) => ({
      ...prev,
      name,
      // Only auto-derive the code while creating and the user hasn't hand-edited it.
      role_code: showCreateModal ? roleCodeFromName(name) : prev.role_code,
    }));
  };

  const handleCreateRole = async () => {
    if (!roleForm.name.trim() || !roleForm.role_code.trim()) {
      setFormError("Role name is required.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      const res = await axios.post(`${API_URL}/api/v1/roles`, {
        name: roleForm.name.trim(),
        role_code: roleForm.role_code.trim(),
        description: roleForm.description.trim(),
        permissions: [],
      });
      const newRoles = await loadRolesAndMatrix();
      const created = newRoles.find((r) => r.role_id === res.data.role_id);
      if (created) setActiveRoleId(created.id);
      setShowCreateModal(false);
    } catch (err) {
      setFormError(err.response?.data?.error || "Couldn't create the role. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRole = async () => {
    if (!activeRole) return;
    if (!roleForm.name.trim()) {
      setFormError("Role name is required.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      await axios.patch(`${API_URL}/api/v1/roles/${activeRole.role_id}`, {
        name: roleForm.name.trim(),
        description: roleForm.description.trim(),
      });
      await loadRolesAndMatrix();
      setShowEditModal(false);
    } catch (err) {
      setFormError(err.response?.data?.error || "Couldn't update the role. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!activeRole) return;
    if (activeRole.systemRole) {
      window.alert("System roles can't be deleted.");
      return;
    }
    if (!window.confirm(`Delete the "${activeRole.name}" role? This can't be undone.`)) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/roles/${activeRole.role_id}`);
      setActiveRoleId(null);
      await loadRolesAndMatrix();
    } catch (err) {
      window.alert(err.response?.data?.error || "Couldn't delete the role. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const filteredCategories = categories.filter((c) =>
    c.label.toLowerCase().includes(permFilter.toLowerCase())
  );

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const all = {};
    categories.forEach((c) => (all[c.id] = true));
    setExpandedRows(all);
  };

  const collapseAll = () => {
    setExpandedRows({});
  };

  if (loading) {
    return (
      <div className="rm-app">
        <RolesManagementStyles />
        <div style={{ padding: 24 }}>Loading roles…</div>
      </div>
    );
  }

  if (loadError || !activeRole) {
    return (
      <div className="rm-app">
        <RolesManagementStyles />
        <div style={{ padding: 24 }}>Couldn't load roles. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="rm-app">
      <RolesManagementStyles />

      <div className="rm-topbar">
        <div className="rm-tabs">
          <button
            className={`rm-tab ${activeTab === "roles" ? "active" : ""}`}
            onClick={() => setActiveTab("roles")}
          >
            Roles
          </button>
          <button
            className={`rm-tab ${activeTab === "permissions" ? "active" : ""}`}
            onClick={() => navigate("/permissions")}
          >
            Permissions
          </button>
        </div>
        <button className="rm-btn rm-btn-primary" onClick={openCreateModal}>
          <Plus size={15} />
          Create Role
        </button>
      </div>

      <div className="rm-body">
        {/* Left: role list */}
        <aside className="rm-sidebar">
          <h2 className="rm-panel-title">Roles</h2>
          <div className="rm-search">
            <Search size={14} className="rm-search-icon" />
            <input
              type="text"
              placeholder="Search roles..."
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            />
          </div>

          <nav className="rm-role-list">
            {filteredRoles.map((role) => {
              const Icon = role.icon;
              const isActive = role.id === activeRoleId;
              return (
                <button
                  key={role.id}
                  className={`rm-role-item ${isActive ? "active" : ""}`}
                  onClick={() => setActiveRoleId(role.id)}
                >
                  <span
                    className="rm-role-icon"
                    style={{ "--icon-bg": role.iconBg, "--icon-fg": role.iconFg }}
                  >
                    <Icon size={15} />
                  </span>
                  <span className="rm-role-text">
                    <span className="rm-role-name-row">
                      <span className="rm-role-name">{role.name}</span>
                      {role.systemRole && <span className="rm-pill">System Role</span>}
                    </span>
                    <span className="rm-role-desc">{role.summary}</span>
                  </span>
                  <span className="rm-role-count">{role.users}</span>
                </button>
              );
            })}
          </nav>

          <div className="rm-sidebar-footer">
            Showing 1 to {filteredRoles.length} of {roles.length} roles
          </div>
        </aside>

        {/* Main column */}
        <main className="rm-main">
          {/* Role details */}
          <section className="rm-card">
            <div className="rm-details-header">
              <div className="rm-details-title">
                <span
                  className="rm-details-avatar"
                  style={{ "--icon-bg": activeRole.iconBg, "--icon-fg": activeRole.iconFg }}
                >
                  <activeRole.icon size={22} />
                </span>
                <h2>Role Details</h2>
              </div>
              <div className="rm-details-actions">
                <button className="rm-btn rm-btn-outline" onClick={openEditModal}>
                  <Pencil size={14} />
                  Edit Role
                </button>
                <button
                  className="rm-btn rm-btn-outline-danger"
                  onClick={handleDeleteRole}
                  disabled={deleting || activeRole.systemRole}
                  title={activeRole.systemRole ? "System roles can't be deleted" : undefined}
                >
                  <Trash2 size={14} />
                  {deleting ? "Deleting…" : "Delete Role"}
                </button>
              </div>
            </div>

            <div className="rm-details-body">
              <div className="rm-fields">
                <div className="rm-field-row">
                  <span className="rm-field-label">Role Name</span>
                  <span className="rm-field-value">{activeRole.name}</span>
                </div>
                <div className="rm-field-row">
                  <span className="rm-field-label">Description</span>
                  <span className="rm-field-value">{activeRole.description}</span>
                </div>
                <div className="rm-field-row">
                  <span className="rm-field-label">Role Type</span>
                  <span className="rm-field-value">
                    <span className="rm-badge-role-type">
                      {activeRole.systemRole ? "System Role" : "Custom Role"}
                    </span>
                  </span>
                </div>
                <div className="rm-field-row">
                  <span className="rm-field-label">Users</span>
                  <span className="rm-field-value">{activeRole.users} users</span>
                </div>
                <div className="rm-field-row">
                  <span className="rm-field-label">Created On</span>
                  <span className="rm-field-value">{activeRole.createdOn}</span>
                </div>
                <div className="rm-field-row">
                  <span className="rm-field-label">Last Updated</span>
                  <span className="rm-field-value">{activeRole.lastUpdated}</span>
                </div>
              </div>

              <div className="rm-users-box">
                <h4>Users with this role</h4>
                {activeRole.userList.map((u) => (
                  <div className="rm-user-row" key={u.email}>
                    <span className="rm-user-avatar">{initials(u.name)}</span>
                    <span className="rm-user-info">
                      <span className="rm-user-name">{u.name}</span>
                      <br />
                      <span className="rm-user-email">{u.email}</span>
                    </span>
                    <span className="rm-status-pill">{u.status}</span>
                  </div>
                ))}
                <a className="rm-view-all" href="#">
                  View all users ({activeRole.users}) <ArrowRight size={13} />
                </a>
              </div>
            </div>
          </section>

          {/* Permissions */}
          <section className="rm-card">
            <div className="rm-perm-toolbar">
              <h3>Permissions</h3>
              <div className="rm-perm-toolbar-actions">
                <button className="rm-btn rm-btn-outline rm-btn-sm" onClick={expandAll}>
                  Expand All
                </button>
                <button className="rm-btn rm-btn-outline rm-btn-sm" onClick={collapseAll}>
                  Collapse All
                </button>
              </div>
            </div>

            <div className="rm-perm-filter-row">
              <div className="rm-search">
                <Search size={14} className="rm-search-icon" />
                <input
                  type="text"
                  placeholder="Search permissions..."
                  value={permFilter}
                  onChange={(e) => setPermFilter(e.target.value)}
                />
              </div>
              <div className="rm-legend-inline">
                <span className="rm-legend-inline-item">
                  <AccessBadge level="full" /> Full Access
                </span>
                <span className="rm-legend-inline-item">
                  <AccessBadge level="partial" /> Partial Access
                </span>
                <span className="rm-legend-inline-item">
                  <AccessBadge level="none" /> No Access
                </span>
              </div>
            </div>

            <div className="rm-table-wrap">
              <table className="rm-table">
                <colgroup>
                  <col className="rm-col-category" />
                  {roleColumns.map((id) => (
                    <col key={id} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="rm-col-category">Permission Category</th>
                    {roleColumns.map((id) => {
                      const role = roles.find((r) => r.id === id);
                      return <th key={id}>{role.name}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((cat) => {
                    const Icon = cat.icon;
                    const isExpanded = !!expandedRows[cat.id];
                    return (
                      <tr key={cat.id}>
                        <td className="rm-col-category">
                          <button
                            className="rm-cat-row"
                            onClick={() => toggleRow(cat.id)}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                          >
                            <ChevronRight
                              size={14}
                              className={`rm-cat-chevron ${isExpanded ? "expanded" : ""}`}
                            />
                            <span className="rm-cat-icon">
                              <Icon size={13} />
                            </span>
                            <span className="rm-cat-label">{cat.label}</span>
                          </button>
                        </td>
                        {roleColumns.map((id) => (
                          <td key={id} className="rm-cell">
                            <AccessBadge level={cat.access[id]} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </main>

        {/* Right sidebar */}
        <aside className="rm-right-sidebar">
          <div className="rm-card rm-info-card">
            <h4>
              <Info size={15} color="#5b4bf5" />
              About Roles
            </h4>
            <p>
              Roles help you define permission sets for users based on their
              responsibilities. You can create custom roles and assign
              granular permissions.
            </p>
          </div>

          <div className="rm-card rm-info-card">
            <h4>Best Practices</h4>
            <ul className="rm-checklist">
              {BEST_PRACTICES.map((tip) => (
                <li key={tip}>
                  <CheckCircle2 size={14} />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          <div className="rm-card rm-info-card">
            <h4>Need Help?</h4>
            <p>Learn more about roles and permissions.</p>
            <div className="rm-help-links">
              <a href="#">
                <BookOpen size={14} /> View Documentation
              </a>
              <a href="#">
                <Headphones size={14} /> Contact Support
              </a>
            </div>
          </div>
        </aside>
      </div>

      {(showCreateModal || showEditModal) && (
        <RoleModal
          isCreate={showCreateModal}
          form={roleForm}
          onField={(field, value) => setRoleForm((prev) => ({ ...prev, [field]: value }))}
          onNameChange={handleNameChange}
          formError={formError}
          submitting={submitting}
          onClose={closeModals}
          onSubmit={showCreateModal ? handleCreateRole : handleEditRole}
        />
      )}
    </div>
  );
}